import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/axios";
import { toast } from "sonner";
import {
  Printer,
  Plus,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
} from "lucide-react";

import { Printer as PrinterType } from "@/types";

interface PrintJobItem {
  id: number;
  fileName: string;
  orderId?: number;
  material?: string;
  color?: string;
  status: "pending" | "printing" | "postprocessing" | "ready";
  printerId?: number;
  printer?: { id?: number; name: string; model?: string };
  gramsEstimated?: number;
  gramsActual?: number;
  notes?: string;
  createdAt: string;
}

const COLUMNS: { key: PrintJobItem["status"]; title: string; color: string }[] = [
  { key: "pending", title: "Por Imprimir", color: "border-amber-500/30 bg-amber-500/5" },
  { key: "printing", title: "En Máquina", color: "border-blue-500/30 bg-blue-500/5" },
  { key: "postprocessing", title: "Post-proceso", color: "border-purple-500/30 bg-purple-500/5" },
  { key: "ready", title: "Listo / Finalizado", color: "border-emerald-500/30 bg-emerald-500/5" },
];

export default function WorkshopPage() {
  const [jobs, setJobs] = useState<PrintJobItem[]>([]);
  const [printers, setPrinters] = useState<PrinterType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewJobOpen, setIsNewJobOpen] = useState(false);
  const [newJob, setNewJob] = useState({
    fileName: "",
    material: "PLA",
    color: "Negro",
    notes: "",
    gramsEstimated: 50,
    printerId: undefined as number | undefined,
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setIsLoading(true);
      const [jobsRes, printersRes] = await Promise.all([
        api.get<PrintJobItem[]>("/workshop/jobs"),
        api.get<PrinterType[]>("/printers").catch(() => ({ data: [] }))
      ]);
      setJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
      setPrinters(Array.isArray(printersRes.data) ? printersRes.data : []);
    } catch {
      toast.error("Error al cargar la cola de producción del taller");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (jobId: number, nextStatus: PrintJobItem["status"]) => {
    try {
      await api.patch(`/workshop/jobs/${jobId}/status`, { status: nextStatus });
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? { ...j, status: nextStatus } : j))
      );
      toast.success(
        nextStatus === "ready"
          ? "¡Trabajo marcado como listo! Filamento descontado del stock."
          : `Estado actualizado a "${nextStatus}"`
      );
    } catch {
      toast.error("No se pudo actualizar el estado del trabajo");
    }
  };

  const handleCreateJob = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newJob.fileName.trim()) {
      toast.error("Por favor ingresa un nombre para el modelo o archivo");
      return;
    }

    try {
      const res = await api.post<PrintJobItem>("/workshop/jobs", {
        fileName: newJob.fileName,
        material: newJob.material,
        color: newJob.color,
        notes: newJob.notes,
        gramsEstimated: Number(newJob.gramsEstimated) || 0,
        printerId: newJob.printerId || null,
        status: "pending",
      });
      if (res.data) {
        setJobs((prev) => [res.data, ...prev]);
        setIsNewJobOpen(false);
        setNewJob({ fileName: "", material: "PLA", color: "Negro", notes: "", gramsEstimated: 50, printerId: undefined });
        toast.success("Trabajo agregado a la cola de impresión");
      }
    } catch {
      toast.error("No se pudo registrar el nuevo trabajo");
    }
  };

  const getNextStatus = (current: PrintJobItem["status"]): PrintJobItem["status"] | null => {
    switch (current) {
      case "pending":
        return "printing";
      case "printing":
        return "postprocessing";
      case "postprocessing":
        return "ready";
      default:
        return null;
    }
  };

  const getPrevStatus = (current: PrintJobItem["status"]): PrintJobItem["status"] | null => {
    switch (current) {
      case "printing":
        return "pending";
      case "postprocessing":
        return "printing";
      case "ready":
        return "postprocessing";
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 h-full flex flex-col pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Taller & Producción 3D</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Tablero Kanban de impresión y gestión de colas de máquinas
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadJobs}
            disabled={isLoading}
            className="gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <Button onClick={() => setIsNewJobOpen(true)} size="sm" className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nuevo Trabajo
          </Button>
        </div>
      </div>

      {/* Kanban Board Columns */}
      <div className="flex-1 overflow-x-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 min-w-[900px] h-full pb-4">
          {COLUMNS.map((col) => {
            const colJobs = jobs.filter((j) => j.status === col.key);

            return (
              <div
                key={col.key}
                className={`flex flex-col rounded-2xl border p-4 ${col.color} backdrop-blur-xs min-h-[500px]`}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-sm tracking-wide text-foreground">
                    {col.title}
                  </h3>
                  <Badge variant="secondary" className="text-xs px-2 py-0.5 font-mono">
                    {colJobs.length}
                  </Badge>
                </div>

                <div className="space-y-3 flex-1 overflow-y-auto pr-1">
                  {colJobs.length === 0 ? (
                    <div className="h-32 border border-dashed border-border/60 rounded-xl flex items-center justify-center text-xs text-muted-foreground/70">
                      Sin piezas en esta etapa
                    </div>
                  ) : (
                    colJobs.map((job) => {
                      const next = getNextStatus(job.status);
                      const prev = getPrevStatus(job.status);

                      return (
                        <Card
                          key={job.id}
                          className="border-border/80 shadow-xs hover:shadow-md transition-all bg-card/95"
                        >
                          <CardContent className="p-3.5 space-y-3">
                            <div className="flex justify-between items-start gap-2">
                              <h4 className="font-medium text-sm leading-snug line-clamp-2">
                                {job.fileName}
                              </h4>
                              {job.orderId && (
                                <Badge variant="outline" className="text-[10px] shrink-0 font-mono">
                                  #{job.orderId}
                                </Badge>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Printer className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                                <select
                                  aria-label="Asignar máquina"
                                  className="bg-transparent text-xs text-foreground font-medium border-0 border-b border-dashed border-border/70 hover:border-primary focus:ring-0 p-0 cursor-pointer max-w-[120px] truncate"
                                  value={job.printerId || ""}
                                  onChange={async (e) => {
                                    const pid = e.target.value ? Number(e.target.value) : undefined;
                                    try {
                                      await api.patch(`/workshop/jobs/${job.id}/status`, {
                                        status: job.status,
                                        printerId: pid || null,
                                      });
                                      const selectedP = printers.find((p) => p.id === pid);
                                      setJobs((prev) =>
                                        prev.map((j) =>
                                          j.id === job.id
                                            ? {
                                                ...j,
                                                printerId: pid,
                                                printer: selectedP ? { id: selectedP.id, name: selectedP.name, model: selectedP.model } : undefined,
                                              }
                                            : j
                                        )
                                      );
                                      toast.success(selectedP ? `Asignado a ${selectedP.name}` : "Impresora desasignada");
                                    } catch {
                                      toast.error("No se pudo reasignar la impresora");
                                    }
                                  }}
                                >
                                  <option value="">Sin asignar</option>
                                  {printers.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium">{job.material || "PLA"}</span>
                                <span className="truncate text-foreground/80 font-normal">
                                  {job.color || "Std"}
                                </span>
                              </div>
                            </div>

                            {job.notes && (
                              <p className="text-[11px] text-muted-foreground bg-muted/30 p-1.5 rounded-md line-clamp-2">
                                {job.notes}
                              </p>
                            )}

                            {/* Acciones de transición rápida */}
                            <div className="flex items-center justify-between pt-2 border-t border-border/40">
                              <div>
                                {prev ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-[11px] text-muted-foreground"
                                    onClick={() => handleStatusChange(job.id, prev)}
                                  >
                                    <ChevronLeft className="h-3.5 w-3.5 mr-0.5" /> Volver
                                  </Button>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground font-mono">
                                    {job.gramsEstimated ? `${job.gramsEstimated}g` : ""}
                                  </span>
                                )}
                              </div>

                              <div>
                                {next && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    className="h-7 px-2.5 text-[11px] gap-1"
                                    onClick={() => handleStatusChange(job.id, next)}
                                  >
                                    Avanzar <ChevronRight className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                {job.status === "ready" && (
                                  <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Listo
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal Nuevo Trabajo */}
      <Dialog open={isNewJobOpen} onOpenChange={setIsNewJobOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleCreateJob}>
            <DialogHeader>
              <DialogTitle>Nuevo Trabajo de Impresión</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-1.5">
                <Label htmlFor="fileName">Nombre del Modelo / Archivo</Label>
                <Input
                  id="fileName"
                  placeholder="Ej: Lámpara Japandi Mediana"
                  value={newJob.fileName}
                  onChange={(e) => setNewJob({ ...newJob, fileName: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="material">Material</Label>
                  <Input
                    id="material"
                    value={newJob.material}
                    onChange={(e) => setNewJob({ ...newJob, material: e.target.value })}
                    placeholder="PLA / PETG"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="color">Color</Label>
                  <Input
                    id="color"
                    value={newJob.color}
                    onChange={(e) => setNewJob({ ...newJob, color: e.target.value })}
                    placeholder="Blanco Hueso"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="grams">Gramos estimados</Label>
                <Input
                  id="grams"
                  type="number"
                  value={newJob.gramsEstimated}
                  onChange={(e) =>
                    setNewJob({ ...newJob, gramsEstimated: Number(e.target.value) })
                  }
                  placeholder="50"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="printerSelect">Asignar Impresora 3D (Opcional)</Label>
                <select
                  id="printerSelect"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newJob.printerId || ""}
                  onChange={(e) =>
                    setNewJob({
                      ...newJob,
                      printerId: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                >
                  <option value="">Por asignar en taller</option>
                  {printers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.status === "idle" ? "Libre" : p.status === "printing" ? "En uso" : "Mantenimiento"})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsNewJobOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit">Encolar Trabajo</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
